import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { getLastWeekRange } from "../../../analytics/modules/getLastWeekRange";
import { getNextWeekRange } from "../getNextWeekRange";
import { calculateTotalRoomNights } from "../../../analytics/modules/calculateTotalRoomNights";
import { calculateTotalADR } from "../../../analytics/modules/calculateTotalADR";
import { calculateTotalRevenue } from "../calculateTotalRevenue";
import { getStartDate } from "../getStartDate";
import { getEndDate } from "../getEndDate";
import { getPreviousYearDate } from "../getPreviousYearDate";

export const useBookings = () => {
  const [lastWeekData, setLastWeekData] = useState({
    bookings: [],
    previousYearBookings: [],
  });
  const [nextWeekData, setNextWeekData] = useState({
    bookings: [],
    previousYearBookings: [],
  });

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const lastWeekDateRange = getLastWeekRange();
        const nextWeekDateRange = getNextWeekRange();

        // Fetch data
        const [
          lastWeekResponse,
          lastWeekPreviousYearResponse,
          nextWeekResponse,
          nextWeekPreviousYearResponse,
        ] = await Promise.all([
          axios.get("/api/bookings/reportbookings", {
            params: {
              startDate: getStartDate(lastWeekDateRange[0]),
              endDate: getEndDate(lastWeekDateRange[1]),
              mode: "stayed",
            },
          }),
          axios.get("/api/bookings/reportbookings", {
            params: {
              startDate: getPreviousYearDate(lastWeekDateRange[0]),
              endDate: getPreviousYearDate(lastWeekDateRange[1]),
              mode: "stayed",
            },
          }),
          axios.get("/api/bookings/reportbookings", {
            params: {
              startDate: getStartDate(nextWeekDateRange[0]),
              endDate: getEndDate(nextWeekDateRange[1]),
              mode: "stayed",
            },
          }),
          axios.get("/api/bookings/reportbookings", {
            params: {
              startDate: getPreviousYearDate(nextWeekDateRange[0]),
              endDate: getPreviousYearDate(nextWeekDateRange[1]),
              mode: "stayed",
            },
          }),
        ]);
        setLastWeekData((previousState) => ({
          ...previousState,
          bookings: lastWeekResponse.data,
          previousYearBookings: lastWeekPreviousYearResponse.data,
        }));
        setNextWeekData((previousState) => ({
          ...previousState,
          bookings: nextWeekResponse.data,
          previousYearBookings: nextWeekPreviousYearResponse.data,
        }));
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
      }
    };
    fetchBookings();
  }, []);

  const lastWeekDataReport = useMemo(
    () => ({
      totalRoomNightsBooked: calculateTotalRoomNights(lastWeekData.bookings),
      totalRoomNightsBookedPreviousYear: calculateTotalRoomNights(
        lastWeekData.previousYearBookings
      ),
      totalRevenueBooked: calculateTotalRevenue(lastWeekData.bookings),
      totalRevenueBookedPreviousYear: calculateTotalRevenue(
        lastWeekData.previousYearBookings
      ),
      totalADRBooked: calculateTotalADR(lastWeekData.bookings),
      totalADRBookedPreviousYear: calculateTotalADR(
        lastWeekData.previousYearBookings
      ),
    }),
    [lastWeekData]
  );

  const nextWeekDataReport = useMemo(
    () => ({
      totalRoomNightsBooked: calculateTotalRoomNights(nextWeekData.bookings),
      totalRoomNightsBookedPreviousYear: calculateTotalRoomNights(
        nextWeekData.previousYearBookings
      ),
      totalRevenueBooked: calculateTotalRevenue(nextWeekData.bookings),
      totalRevenueBookedPreviousYear: calculateTotalRevenue(
        nextWeekData.previousYearBookings
      ),
      totalADRBooked: calculateTotalADR(nextWeekData.bookings),
      totalADRBookedPreviousYear: calculateTotalADR(
        nextWeekData.previousYearBookings
      ),
    }),
    [nextWeekData]
  );

  return { lastWeekDataReport, nextWeekDataReport };
};
